/**
 * Rendering metrics. TypeScript owns these values and writes them onto the DOM
 * as CSS custom properties, so the ported mockup stylesheet keeps reading
 * `var(--period-lane-h)` while there is still a single source of truth.
 */

export interface Metrics {
  /** Screen only. Print derives its own step from the page width (plan §5.1). */
  pxPerTick: number;
  periodLaneH: number;
  eventLaneH: number;
  /** White space between two bars in adjacent lanes. */
  barGap: number;
  bandGap: number;
  /** Extra axis height per staggered row of tick labels (plan §5.3). */
  labelRowH: number;
  /** The band's title sits above its body — zero when it sits in the gutter. */
  bandLabelH: number;
  /** A side column holding the band's name, to its left. Zero on screen. */
  gutterW: number;
  tickLen: number;
  majorTickLen: number;
  /**
   * Clear space between the dates and the first bar below. Without it the two
   * touch, and a date reads as if it belonged to the bar.
   */
  axisPad: number;
}

export const SCREEN_METRICS: Metrics = {
  pxPerTick: 34,
  periodLaneH: 20,
  eventLaneH: 18,
  barGap: 3,
  bandGap: 28,
  labelRowH: 10,
  bandLabelH: 18,
  gutterW: 0,
  tickLen: 6,
  majorTickLen: 9,
  axisPad: 6,
};

/** Matches `.tick-label` in timeline.css, which pins its line-height to 1. */
export const TICK_LABEL_FONT_PX = 9;

/** Where the row of tick labels starts, just under the longest tick. */
export function axisLabelTop(metrics: Metrics): number {
  return metrics.majorTickLen + 1;
}

/**
 * The axis is one line of labels, with clearance under it — always. Labels that
 * do not fit are dropped rather than stacked, so this never varies with the
 * content; only the metrics move it.
 */
export function axisHeight(metrics: Metrics): number {
  return axisLabelTop(metrics) + TICK_LABEL_FONT_PX + metrics.axisPad;
}

/* --- Event markers ---------------------------------------------------------- */

/**
 * The box an event label occupies: 10 px of type (`.event` in timeline.css) plus
 * room for the descenders of a "g" or a closing bracket, which a `line-height: 1`
 * box does not contain.
 */
export const EVENT_LABEL_H = 12;

/** The uncertainty bracket, drawn under the label. */
export const WHISKER_H = 3;

/**
 * Where the bracket sits inside its lane. Pinned to the bottom so the gap under
 * the label is whatever the lane has left — the label itself never moves, and an
 * event lane is sized to leave at least a couple of pixels here.
 */
export function whiskerTop(metrics: Metrics, laneTop: number): number {
  return laneTop + metrics.eventLaneH - WHISKER_H;
}

/** Clear space between an event label and the bracket below it. */
export function whiskerGap(metrics: Metrics): number {
  return metrics.eventLaneH - WHISKER_H - EVENT_LABEL_H;
}

/** A bar narrower than this would vanish; La Peste (one year) lives here. */
export const MIN_BAR_PX = 3;

/** Below this, an uncertainty whisker is noise rather than information. */
export const MIN_WHISKER_PX = 8;

/**
 * Print is always tight, and every value here is at its floor rather than at a
 * comfortable setting. Measured against ReperesTemporels.ods, which is the same
 * chart drawn by hand: the spreadsheet prints a row every 13 px, the screen
 * metrics spend 18.6 px, and the difference is all leading — the type is the
 * same size or smaller.
 *
 * What was cut, and why each is safe:
 *
 * - `bandLabelH: 0`. The name moves into `gutterW`, beside the axis, the way
 *   the spreadsheet puts it in its own column 1. Worth 208 px on that chart.
 * - `bandGap: 2`. Bands are already separated by a rule.
 * - `periodLaneH: 13` with `barGap: 2`, so a bar keeps the 11 px box it had at
 *   a lane of 15 and a gap of 3: same bar, one pixel less white above it.
 * - `eventLaneH` stays at 17. Below that the uncertainty bracket touches the
 *   label above it (`whiskerGap`), which is the one thing here that is not air.
 * - `axisPad: 4` and shorter ticks. The dates keep clear of the first bar, with
 *   less room than on screen because paper is the scarce thing.
 *
 * Nothing touches the type: 10 px on bars, 9 px on graduations, as on screen.
 * The printed spreadsheet reads at about 10.6 px, so the app is already below
 * it — shrinking the text would buy space at a cost the leading pays better.
 */
export const PRINT_METRICS: Metrics = {
  pxPerTick: 34,
  periodLaneH: 13,
  eventLaneH: 17,
  barGap: 2,
  bandGap: 2,
  labelRowH: 8,
  bandLabelH: 0,
  gutterW: 76,
  tickLen: 5,
  majorTickLen: 7,
  axisPad: 4,
};

export function applyMetrics(el: HTMLElement, metrics: Metrics): void {
  el.style.setProperty('--px-per-tick', String(metrics.pxPerTick));
  el.style.setProperty('--period-lane-h', String(metrics.periodLaneH));
  el.style.setProperty('--event-lane-h', String(metrics.eventLaneH));
  el.style.setProperty('--bar-gap', String(metrics.barGap));
  el.style.setProperty('--band-gap', String(metrics.bandGap));
  el.style.setProperty('--label-row-h', String(metrics.labelRowH));
  el.style.setProperty('--band-label-h', String(metrics.bandLabelH));
  el.style.setProperty('--gutter-w', String(metrics.gutterW));
}
